import "primeicons/primeicons.css";
import "primereact/resources/primereact.min.css";
import "primereact/resources/themes/lara-light-blue/theme.css"; // or any theme
import { ReactElement, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import AboutUs from "./components/AboutUs";
import AdminDashboard from "./components/AdminDashboard";
import ForgotPasswordPage from "./components/Auth/ForgotPasswordPage";
import ResetPasswordPage from "./components/Auth/ResetPasswordPage";
import PropertyListBB from "./components/B2B/PropertyListBB";
import Clients from "./components/Clients";
import ContactUs from "./components/ContactUs";
import Dashboard from "./components/Dashboard";
import LayoutWrapper from "./components/LayoutWrapper";
import ListProperties from "./components/ListProperties";
import ViewProperty from "./components/ListProperties/ViewProperty";
import Login from "./components/Login";
import AddEditProperty from "./components/ManageProperty/AddEditProperty";
import Property from "./components/ManageProperty/Property";
import Amenities from "./components/Masters/Amenities/Amenities";
import ChildAmenities from "./components/Masters/Amenities/ChildAmenities";
import CityComponent from "./components/Masters/City";
import ClientMasterComponent from "./components/Masters/ClientMaster";
import Complementary from "./components/Masters/Complementary";
import CountryComponent from "./components/Masters/Country";
import Facility from "./components/Masters/Facility";
import LocationComponent from "./components/Masters/Locations";
import RoomTypeComponent from "./components/Masters/RoomType";
import StatesComponent from "./components/Masters/States";
import TierMaster from "./components/Masters/Tier";
import Type from "./components/Masters/Type";
import ZoneComponent from "./components/Masters/Zones";
import MyProfile from "./components/MyProfile";
import Navigation from "./components/Navigation";
import Properties from "./components/Properties"; // legacy if needed
import Proposal from "./components/Proposal";
import ProposalWizard from "./components/ProposalWizard";
import RMCAdminDashboard from "./components/RMCAdminDashboard";
import RMCProposalManagement from "./components/RMCProposalManagement/RMCProposalManagement";
import Reservation from "./components/Reservation";
import ReservationThankYou from "./components/ReservationThankYou/ReservationThankYou";
import ReservePage from "./components/Reservations/ReservePage";
import RmcGrid from "./components/RmcGrid";
import RoomTypeCategories from "./components/RoomTypeCategories";
import RoomsPage from "./components/RoomsPage";
import Signup from "./components/Signup";
import SmartHome from "./components/SmartHome";
import Users from "./components/Users";
import Vendor from "./components/Vendor";
import { MilestoneProvider } from "./contexts/MilestoneContext";
import { rehydrateAuth } from "./reduxToolkit/reducers/layout/authSlice";
import { AppDispatch, RootState } from "./reduxToolkit/store";

type PrivateRouteProps = {
  children: ReactElement;
  allowedRoles?: string[];
};

const PrivateRoute = ({ children, allowedRoles }: PrivateRouteProps) => {
  const token = useSelector((state: RootState) => state.auth.token);
  const userRole = useSelector((state: RootState) => state.auth.userRole);
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );
  const authHydrated = useSelector(
    (state: RootState) => (state as any).auth.hydrated
  );

  console.log("PrivateRoute check:", {
    token: !!token,
    isAuthenticated,
    userRole,
    allowedRoles,
  });

  // If auth isn't hydrated yet, don't redirect — let the app finish rehydration first.
  if (!authHydrated) {
    return <>{children}</>; // render children (or spinner); avoid navigation while rehydrating
  }

  if (!token || !isAuthenticated) {
    console.log("Redirecting to login - no token or not authenticated");
    return <Navigate to="/login" />;
  }

  if (allowedRoles && (!userRole || !allowedRoles.includes(userRole))) {
    console.log(
      `Access denied - user role "${userRole}" not in allowed roles:`,
      allowedRoles
    );
    return <Navigate to="/" />; // or a 403 page
  }

  return children;
};

const AppRoutes = () => {
  const location = useLocation();
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/home" element={<SmartHome />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/login" element={<Login />} />
      <Route path="/password-reset" element={<ResetPasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      {/* New admin-prefixed routes */}
      <Route
        path="/admin/dashboard"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <AdminDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/rmc/dashboard"
        element={
          <PrivateRoute allowedRoles={["RMCAdmin", "RMCUser"]}>
            <RMCAdminDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/rmc"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <RmcGrid />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <PrivateRoute>
            <Users />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/vendors"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <Vendor />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/property"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <Property />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/property/:propertyId/rooms"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <RoomsPage />
          </PrivateRoute>
        }
      />
      {/* Public/SEO or non-admin rooms view (edit restricted by role) */}
      <Route
        path="/property/:propertyId/rooms"
        element={
          <PrivateRoute>
            <RoomsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/amenities"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <Amenities />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/facility"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <Facility />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/complementary"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <Complementary />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/reservation"
        element={
          <PrivateRoute allowedRoles={["Administrator", "Consultant"]}>
            <Reservation />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/countries"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <CountryComponent />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/locations"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <LocationComponent />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/zones"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <ZoneComponent />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/room-types"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <RoomTypeComponent />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/room-type-categories"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <RoomTypeCategories />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/property-types"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <Type />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/tier"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <TierMaster />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/clientsmaster"
        element={
          <PrivateRoute allowedRoles={["Administrator"]}>
            <ClientMasterComponent />
          </PrivateRoute>
        }
      />
      <Route
        path="/rmc/proposal-management"
        element={
          <PrivateRoute allowedRoles={["RMCAdmin"]}>
            <RMCProposalManagement />
          </PrivateRoute>
        }
      />

      {/* Backward compatibility redirects (to be removed later) */}
      <Route
        path="/admin-dashboard"
        element={<Navigate to="/admin/dashboard" replace />}
      />
      <Route path="/rmc" element={<Navigate to="/admin/rmc" replace />} />
      <Route path="/Users" element={<Navigate to="/admin/users" replace />} />
      <Route
        path="/Vendors"
        element={<Navigate to="/admin/vendors" replace />}
      />
      <Route
        path="/Property"
        element={<Navigate to="/admin/property" replace />}
      />
      <Route
        path="/Amenities"
        element={<Navigate to="/admin/amenities" replace />}
      />
      <Route
        path="/Facility"
        element={<Navigate to="/admin/facility" replace />}
      />
      <Route
        path="/Complementary"
        element={<Navigate to="/admin/complementary" replace />}
      />
      <Route
        path="/reservation"
        element={<Navigate to="/admin/reservation" replace />}
      />
      <Route
        path="/countries"
        element={<Navigate to="/admin/countries" replace />}
      />
      <Route
        path="/room-type-categories"
        element={<Navigate to="/admin/room-type-categories" replace />}
      />
      <Route path="/Tier" element={<Navigate to="/admin/tier" replace />} />
      <Route
        path="/clientsmaster"
        element={<Navigate to="/admin/clientsmaster" replace />}
      />
      {/* Backward compatibility route (deprecated) */}
      <Route
        path="/clients"
        element={
          <PrivateRoute>
            <Clients />
          </PrivateRoute>
        }
      />
      <Route
        path="/rmc/:rmcId/clients"
        element={
          <PrivateRoute>
            <Clients />
          </PrivateRoute>
        }
      />
      {/* Nested clients under RMC */}
      <Route
        path="/rmc/:rmcId/clients"
        element={
          <PrivateRoute>
            <Clients />
          </PrivateRoute>
        }
      />
      {/* RMC / consultant (legacy) routes remain below; consider namespacing later */}

      <Route path="/list-properties" element={<ListProperties />} />
      <Route path="/Property" element={<Property />} />
      {/* Above admin-prefixed replacements exist; these legacy definitions kept for transitional compatibility */}

      <Route
        path="/manage-property/add"
        element={<AddEditProperty {...(location.state || {})} />}
      />
      <Route
        path="/manage-property/edit/:propertyId"
        element={<AddEditProperty {...(location.state || {})} />}
      />
      <Route path="/properties" element={<Properties />} />
      <Route path="/proposals" element={<Proposal />} />
      <Route
        path="/rmc/:rmcId/clients/:clientId/proposal"
        element={<Proposal />}
      />
      <Route path="/proposal-wizard" element={<ProposalWizard />} />
      <Route
        path="/rmc/:rmcId/clients/:clientId/proposal-wizard"
        element={<ProposalWizard />}
      />
      <Route
        path="/rmc/:rmcId/clients/:clientId/properties/:propertyId/rooms"
        element={<RoomsPage />}
      />
      {/* SEO friendly property route */}
      <Route path="/property/:propertySlug" element={<ViewProperty />} />
      {/* Legacy fallback */}
      <Route path="/view-property" element={<ViewProperty />} />
      <Route path="/b2b-property" element={<PropertyListBB />} />
      <Route path="/about-us" element={<AboutUs />} />
      <Route path="/cities" element={<CityComponent />} />
      <Route path="/locations" element={<LocationComponent />} />
      <Route path="/countries" element={<CountryComponent />} />
      <Route path="/states" element={<StatesComponent />} />
      <Route path="/child-amenities" element={<ChildAmenities />} />
      <Route path="/contact-us" element={<ContactUs />} />
      <Route path="/room-type-categories" element={<RoomTypeCategories />} />
      <Route
        path="/my-profile"
        element={
          <PrivateRoute>
            <MyProfile />
          </PrivateRoute>
        }
      />
      <Route path="/reserve" element={<ReservePage />} />
      <Route path="/reservation-thank-you" element={<ReservationThankYou />} />
      <Route path="/clientsmaster" element={<ClientMasterComponent />} />
      <Route path="/Tier" element={<TierMaster />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

const App = () => {
  const dispatch = useDispatch<AppDispatch>();
  const authHydrated = useSelector((state: RootState) => (state as any).auth.hydrated);
  useEffect(() => {
    dispatch(rehydrateAuth());
  }, [dispatch]);

  return (
    <MilestoneProvider>
      <BrowserRouter>
        <div className="App">
          <Navigation />
          <LayoutWrapper>
            <AppRoutes />
          </LayoutWrapper>
        </div>
      </BrowserRouter>
    </MilestoneProvider>
  );
};

export default App;
